import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";

interface Context {
  orgId: string;
  storeOrg: (orgId: string) => void;
  removeOrg: () => void;
  isLoading: boolean;
}

const OrgContext = createContext<Context>({} as Context);

function OrgProvider({ children }: { children: ReactNode }) {
  const [isLoading, setIsLoading] = useState(false);
  const [org, setOrg] = useState<{ orgId: string | undefined }>(() => {
    const orgId = window.localStorage.getItem("current_org");
    return {
      orgId:
        orgId !== null
          ? (JSON.parse(orgId as string) as { orgId: string }).orgId
          : undefined,
    };
  });

  useEffect(() => {
    setIsLoading(true);
    const currentOrg = window.localStorage.getItem("current_org");
    if (currentOrg) {
      const parsed = JSON.parse(currentOrg) as { orgId: string };

      setOrg(parsed);
    }
    setIsLoading(false);
  }, []);

  const storeOrg = (orgId: string) => {
    // Store synchronously to localStorage
    window.localStorage.setItem(
      "current_org",
      JSON.stringify({
        orgId: orgId,
      }),
    );

    // Update state synchronously
    setOrg({
      orgId: orgId,
    });
  };

  const removeOrg = () => {
    window.localStorage.removeItem("current_org");
    setOrg({
      orgId: "",
    });
  };

  return (
    <OrgContext
      value={{
        orgId: org.orgId ?? "",
        storeOrg,
        removeOrg,
        isLoading,
      }}
    >
      {children}
    </OrgContext>
  );
}

function useOrg() {
  const context = useContext(OrgContext);
  if (!context) {
    throw new Error("useOrg must be used within an OrganizationProvider");
  }

  return context;
}

export { useOrg, OrgProvider };
